/**
 * The arithmetic behind the image viewer's zoom, pan and swipes, kept pure so
 * it can be tested without a browser. Offsets are in CSS pixels from the centre
 * of the stage; the image is drawn fitted at scale 1 and transformed with
 * `translate(x, y) scale(scale)` about its own centre.
 */

export const MIN_SCALE = 1;
export const MAX_SCALE = 5;
/** What a double-tap or a click zooms to. */
export const TAP_SCALE = 2.5;

export interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const IDENTITY: ZoomState = { scale: 1, x: 0, y: 0 };

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return MIN_SCALE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * Keeps the image covering the stage wherever it is larger than it, and centred
 * on any axis where it is not — so a pan can never push the picture off-screen
 * and leave an empty stage.
 */
export function clampPan(state: ZoomState, image: Size, stage: Size): ZoomState {
  const scale = clampScale(state.scale);
  const limitX = Math.max(0, (image.width * scale - stage.width) / 2);
  const limitY = Math.max(0, (image.height * scale - stage.height) / 2);
  return {
    scale,
    // `+ 0` turns a -0 into 0, which reads the same and compares equal.
    x: clamp(state.x, -limitX, limitX) + 0,
    y: clamp(state.y, -limitY, limitY) + 0,
  };
}

/**
 * Zooms to `nextScale` keeping the image point under `point` (relative to the
 * stage centre) where it is, which is what makes a wheel or a pinch feel
 * anchored to the cursor or the fingers rather than to the middle.
 */
export function zoomAt(state: ZoomState, nextScale: number, point: { x: number; y: number }, image: Size, stage: Size): ZoomState {
  const scale = clampScale(nextScale);
  const ratio = scale / state.scale;
  return clampPan(
    {
      scale,
      x: point.x - (point.x - state.x) * ratio,
      y: point.y - (point.y - state.y) * ratio,
    },
    image,
    stage,
  );
}

/** A tap toggles between fitted and `TAP_SCALE` at the point tapped. */
export function toggleZoom(state: ZoomState, point: { x: number; y: number }, image: Size, stage: Size): ZoomState {
  return state.scale > 1.01 ? IDENTITY : zoomAt(state, TAP_SCALE, point, image, stage);
}

/** The wheel as a zoom factor: smooth for a trackpad, a notch at a time for a mouse. */
export function wheelFactor(deltaY: number, deltaMode = 0): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return Math.exp(-clamp(pixels, -200, 200) * 0.0025);
}

/** Index arithmetic for a group that wraps at both ends. */
export function wrapIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return ((Math.trunc(index) % count) + count) % count;
}

export type SwipeIntent = "next" | "prev" | "close" | null;

/**
 * What a one-finger drag on an unzoomed image meant once it is let go: far or
 * fast enough sideways is a page, far or fast enough downwards is a close, and
 * anything else springs back. `ms` is how long the finger was down.
 */
export function swipeIntent(dx: number, dy: number, ms: number, canPage: boolean): SwipeIntent {
  const time = Math.max(ms, 1);
  const horizontal = Math.abs(dx) > Math.abs(dy);
  if (horizontal) {
    if (!canPage) return null;
    const fast = Math.abs(dx) / time > 0.5 && Math.abs(dx) > 24;
    if (Math.abs(dx) > 70 || fast) return dx < 0 ? "next" : "prev";
    return null;
  }
  const fastDown = dy / time > 0.6 && dy > 30;
  if (dy > 110 || fastDown) return "close";
  return null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
